import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Star, TrendingDown, Award } from "lucide-react";
import type { Bid } from "@/lib/types";
import { cn } from "@/lib/utils";

interface BidCardProps {
	bid: Bid;
	rank: number;
	lowestAmount: number;
	budget: number;
	reputationScore: number;
}

export function BidCard({ bid, rank, lowestAmount, budget, reputationScore }: BidCardProps) {
	const savings = budget - bid.amount;
	const savingsPercent = Math.round((savings / budget) * 100);
	const isLowest = bid.amount === lowestAmount;
	const timeAgo = getTimeAgo(bid.createdAt);

	return (
		<Card
			className={cn(
				"transition-all hover:shadow-md",
				rank === 1 && "ring-2 ring-primary/20 shadow-sm"
			)}
		>
			<CardContent className="p-4">
				<div className="flex items-start gap-4">
					<div className="flex flex-col items-center gap-1">
						<div
							className={cn(
								"text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center",
								rank === 1
									? "bg-primary text-primary-foreground"
									: rank === 2
										? "bg-muted-foreground/20 text-foreground"
										: "bg-muted text-muted-foreground"
							)}
						>
							{rank}
						</div>
						<Tooltip>
							<TooltipTrigger>
								<Avatar className="h-10 w-10">
									<AvatarFallback
										className={cn(
											"text-xs font-semibold",
											bid.provider.rating >= 4.8
												? "bg-emerald-100 text-emerald-700"
												: bid.provider.rating >= 4.5
													? "bg-blue-100 text-blue-700"
													: "bg-muted text-muted-foreground"
										)}
									>
										{bid.provider.avatar}
									</AvatarFallback>
								</Avatar>
							</TooltipTrigger>
							<TooltipContent>
								<p className="font-medium">{bid.provider.name}</p>
								<p className="text-xs text-muted-foreground">
									{bid.provider.completedJobs} jobs completed
								</p>
							</TooltipContent>
						</Tooltip>
					</div>

					<div className="flex-1 min-w-0">
						<div className="flex items-center justify-between mb-1">
							<div className="flex items-center gap-2">
								<span className="font-semibold text-sm">
									{bid.provider.name}
								</span>
								<div className="flex items-center gap-0.5">
									<Star className="h-3 w-3 fill-amber-400 text-amber-400" />
									<span className="text-xs font-medium">
										{bid.provider.rating.toFixed(1)}
									</span>
								</div>
								{bid.provider.rating >= 4.8 && (
									<Badge variant="secondary" className="text-[10px] h-4 px-1.5 gap-0.5">
										<Award className="h-2.5 w-2.5" />
										Top Rated
									</Badge>
								)}
							</div>
							<span className="text-xs text-muted-foreground">{timeAgo}</span>
						</div>

						<p className="text-sm text-muted-foreground line-clamp-2 mb-2">
							{bid.message}
						</p>

						<div className="flex items-center gap-3 flex-wrap">
							<div className="flex items-baseline gap-1">
								<span className="text-xl font-bold tabular-nums">
									${bid.amount.toLocaleString()}
								</span>
							</div>

							{isLowest && (
								<Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px] h-5">
									<TrendingDown className="h-3 w-3 mr-0.5" />
									Lowest Bid
								</Badge>
							)}

							{savings > 0 && (
								<span className="text-xs text-emerald-600 font-medium">
									{savingsPercent}% under budget
								</span>
							)}

							<Tooltip>
								<TooltipTrigger>
									<Badge variant="outline" className="text-[10px] h-5 tabular-nums">
										Score: {reputationScore.toFixed(1)}
									</Badge>
								</TooltipTrigger>
								<TooltipContent>
									<p className="text-xs">
										Reputation-weighted score combining price competitiveness and provider rating
									</p>
								</TooltipContent>
							</Tooltip>
						</div>
					</div>
				</div>
			</CardContent>
		</Card>
	);
}

function getTimeAgo(dateStr: string): string {
	const diff = Date.now() - new Date(dateStr).getTime();
	const mins = Math.floor(diff / 60000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	return `${Math.floor(hrs / 24)}d ago`;
}
